class GlobalCollectionSort extends HTMLElement {
  connectedCallback() {
    this.abortController = new AbortController();
    this.sortProducts();
  }

  disconnectedCallback() {
    this.abortController?.abort();
  }

  async sortProducts() {
    const list = document.getElementById(this.dataset.listId);

    if (
      !list ||
      list.dataset.globalSortReady === 'true' ||
      list.dataset.globalSortReady === 'loading'
    ) {
      return;
    }

    list.dataset.globalSortReady = 'loading';

    const currentPage = Number(list.dataset.currentPage);
    const pageSize = Number(list.dataset.pageSize);
    const totalPages = Number(list.dataset.totalPages);
    const pagesToLoad = [];

    for (let page = 1; page <= totalPages; page += 1) {
      pagesToLoad.push(page);
    }

    const loadPage = async (page) => {
      const url = new URL(this.dataset.collectionUrl, window.location.origin);

      for (const [key, value] of new URL(window.location.href).searchParams) {
        if (key.startsWith('filter.')) {
          url.searchParams.append(key, value);
        }
      }

      url.searchParams.set('page', page);
      url.searchParams.set('sort_by', 'created-descending');
      url.searchParams.set('section_id', this.dataset.sectionId);

      const response = await fetch(url.toString(), {
        credentials: 'same-origin',
        signal: this.abortController.signal
      });

      if (!response.ok) {
        throw new Error(`Unable to load collection page ${page}`);
      }

      const html = await response.text();
      const documentForPage = new DOMParser().parseFromString(html, 'text/html');
      return {
        page,
        cards: [...documentForPage.querySelectorAll('[data-global-sort-list] > product-card')]
      };
    };

    try {
      const pageCards = [];
      const workerCount = Math.min(3, pagesToLoad.length);

      const workers = Array.from({ length: workerCount }, async () => {
        while (pagesToLoad.length > 0) {
          const page = pagesToLoad.shift();
          pageCards.push(await loadPage(page));
        }
      });

      await Promise.all(workers);

      if (!this.isConnected || !list.isConnected) {
        return;
      }

      pageCards.sort((firstPage, secondPage) => firstPage.page - secondPage.page);

      const cards = pageCards.flatMap((pageResult) => pageResult.cards);
      const uniqueCards = [...new Map(cards.map((card) => [card.getAttribute('handle'), card])).values()];
      const availablePageCount = Math.max(1, Math.ceil(uniqueCards.length / pageSize));

      if (currentPage > availablePageCount) {
        const lastAvailablePageUrl = new URL(window.location.href);
        lastAvailablePageUrl.searchParams.set('page', availablePageCount);
        window.location.replace(lastAvailablePageUrl.toString());
        return;
      }

      const pagination = list.closest('.collection__main')?.querySelector('.pagination');

      for (const link of pagination?.querySelectorAll('a[href]') || []) {
        const linkPage = Number(new URL(link.href).searchParams.get('page') || 1);

        if (linkPage > availablePageCount) {
          (link.closest('facet-link') || link).remove();
        }
      }

      uniqueCards.sort((firstCard, secondCard) => {
        const firstTimestamp = Number(firstCard.dataset.globalSortTimestamp);
        const secondTimestamp = Number(secondCard.dataset.globalSortTimestamp);
        const firstContinuesSelling = firstCard.dataset.globalSortContinueSelling === 'true';
        const secondContinuesSelling = secondCard.dataset.globalSortContinueSelling === 'true';

        if (firstContinuesSelling !== secondContinuesSelling) {
          return firstContinuesSelling ? -1 : 1;
        }

        if (firstContinuesSelling && secondContinuesSelling) {
          return secondTimestamp - firstTimestamp;
        }

        const sizeDifference =
          Number(secondCard.dataset.globalSortSizeCount) -
          Number(firstCard.dataset.globalSortSizeCount);

        if (sizeDifference !== 0) {
          return sizeDifference;
        }

        return secondTimestamp - firstTimestamp;
      });

      const startIndex = (currentPage - 1) * pageSize;
      const cardsForCurrentPage = uniqueCards.slice(startIndex, startIndex + pageSize);

      for (const card of cardsForCurrentPage) {
        card.removeAttribute('reveal-on-scroll');
        card.style.opacity = '1';
        card.style.transform = 'none';
      }

      list.replaceChildren(...cardsForCurrentPage);
      list.dataset.globalSortReady = 'true';
    } catch (error) {
      list.dataset.globalSortReady = 'false';

      if (error.name !== 'AbortError') {
        console.error('Global collection sorting failed:', error);
      }
    }
  }
}

if (!customElements.get('global-collection-sort')) {
  customElements.define('global-collection-sort', GlobalCollectionSort);
}
